import { ReactNode, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Grid, Container, Divider, Image } from 'semantic-ui-react'
import { FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'
import { DateTime } from 'luxon'

import {
  Button,
  FormikDateInput,
  FormikLabelError,
  FormikRadioButton,
  FormikRadioToggleButton,
  getFieldName,
  GridRowColumn,
  makeReqBoolSchema,
  makeReqStringSchema,
  Text,
} from '../../../components/BaseComponents'
import SignupHeader from '../SignupHeader'
import { updateFinancialProfile } from '../../../actions/financialProfileActions'
import {
  getCurrentUser,
  getFinancialProfile,
  getFreeTrialStatus,
} from '../../../selectors/user.selectors'
import Popup from '../../../components/BaseComponents/Popup'
import { SIGNUP_PATHS, C_CORP_TAX_ENTITY, isFreeTrialEnabled } from '../helpers'
import { useSignupPageUpdate } from '../../../services/onboardingService'
import { TAX_ENTITY_TYPES } from '../../Taxes/taxConstants'
import { useAnalyticsView } from '../../Amplitude'
import {
  useNavigateWithPersistParams,
  useReselector,
  useScrollRef,
} from '../../../utils/sharedHooks'
import {
  ReferralStatus,
  updateUserReferralStatusIfExists,
} from '../../Referrals/referrals.slice'
import { useAppDispatch } from '../../../utils/typeHelpers'
import { DATE_FORMATS, DATE_FORMATS_LUXON } from '../../../utils/dateHelpers'
import { selectApproximatePracticeStartDate } from '../../../selectors/catchupBookkeeping.selectors'
import FreeTrialSignupBanner from '../FreeTrialSignupBanner'
import { fetchAllAnnualTaxDetailsIfNeeded } from '../../Admin/AnnualTaxDetails/annualTaxDetails.slice'
import { generateBookkeepingReport } from '../../../actions/bookkeepingReportActions'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { FEATURE_FLAG_KEYS } from '../../OpenFeature'
import { PracticeType } from '@/constants/onboardingConstants'

/** Including self (so add 1) */
enum NumberOfEmployees {
  justMe = '1',
  twoToTen = '2-10',
  tenOrMore = '10+',
}

const QuestionRow = ({
  name,
  label,
  popupContent,
  schema,
  required,
}: {
  name: string
  label: string
  popupContent: ReactNode
  schema?: yup.AnySchema
  required?: boolean
}) => (
  <>
    <GridRowColumn>
      <FormikLabelError
        name={name}
        label={label}
        required
        afterLabel={<Popup content={popupContent} />}
        schema={
          schema
            ? schema
            : required
              ? yup.boolean().nullable().required()
              : undefined
        }
      />
    </GridRowColumn>
    <Grid.Row className="short">
      <Grid.Column width={3}>
        <FormikRadioToggleButton name={name} value fullWidth>
          Yes
        </FormikRadioToggleButton>
      </Grid.Column>
      <Grid.Column width={3}>
        <FormikRadioToggleButton name={name} value={false} fullWidth>
          No
        </FormikRadioToggleButton>
      </Grid.Column>
    </Grid.Row>
  </>
)

const AboutYourPractice = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigateWithPersistParams()
  const financialProfile = useReselector(getFinancialProfile)
  const pageView = useAnalyticsView()
  const { scrollRef, scrollToRef } = useScrollRef({ autoScroll: true })
  const currentUser = useReselector(getCurrentUser)
  const approximatePracticeStartDate = useReselector(
    selectApproximatePracticeStartDate
  )

  const [searchParams] = useSearchParams()
  const basicPlanSearchParamValue = searchParams.get('subscription')
  const useBasicTypePlan =
    basicPlanSearchParamValue === 'new' || basicPlanSearchParamValue === 'basic'
  const userFreeTrialStatus = useReselector(getFreeTrialStatus)
  const freeTrialEnabled = isFreeTrialEnabled(
    searchParams.get('trial'),
    userFreeTrialStatus,
    useBasicTypePlan
  )
  const shouldEnableAutoReconCUBK2 = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableAutoreconCUBK2,
    false
  )
  const enableStarterPlans = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableStarterPlans,
    false
  )
  useEffect(() => {
    dispatch(fetchAllAnnualTaxDetailsIfNeeded())
  }, [dispatch])

  const { email } = currentUser || {}
  useEffect(() => {
    if (email) {
      updateUserReferralStatusIfExists({
        recipientEmail: email,
        status: ReferralStatus.account_created,
      })(dispatch)
    }
  }, [email, dispatch])

  useEffect(() => {
    pageView('sign up about your practice')
  }, [pageView])

  useEffect(() => {
    scrollToRef()
  }, [scrollToRef])

  useSignupPageUpdate(SIGNUP_PATHS.aboutYourPractice)

  const formik = useFormik({
    initialValues: {
      taxEntityType: financialProfile?.taxEntityType as
        | TAX_ENTITY_TYPES
        | 'other',
      businessEntity: financialProfile?.businessEntity || undefined,
      accountsSeparate: financialProfile?.accountsSeparate,
      willingAccountsSeparate: financialProfile?.willingAccountsSeparate,
      multipleOwners: null as boolean | null,
      over10Employees: null as boolean | null,
      scorpEntireYear: null as boolean | null,
      fileInternationalTaxes: financialProfile?.fileInternationalTaxes,
      approximatePracticeStartDate:
        approximatePracticeStartDate?.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH) ||
        '',
      numberOfEmployees:
        financialProfile?.practiceType === PracticeType.solo
          ? NumberOfEmployees.justMe
          : financialProfile?.practiceType === PracticeType.group
            ? NumberOfEmployees.twoToTen
            : '',
    },
    validateOnMount: true,
    onSubmit: async ({
      taxEntityType,
      businessEntity,
      accountsSeparate,
      willingAccountsSeparate,
      multipleOwners,
      over10Employees,
      fileInternationalTaxes,
      approximatePracticeStartDate,
      scorpEntireYear,
      numberOfEmployees,
    }) => {
      const otherDisq =
        taxEntityType === 'other' &&
        businessEntity &&
        ['llc_single', 'c_corp', 'other'].includes(businessEntity)

      const navToConsult =
        businessEntity === 'other' &&
        ['form_1040', 'form_1120_s'].includes(taxEntityType)

      if (
        taxEntityType === C_CORP_TAX_ENTITY ||
        (!accountsSeparate && !willingAccountsSeparate) ||
        multipleOwners ||
        fileInternationalTaxes ||
        scorpEntireYear === false ||
        otherDisq
      ) {
        navigate(SIGNUP_PATHS.disqualified, {
          hasInternationalTaxes: Boolean(fileInternationalTaxes).toString(),
          multipleOwners: Boolean(multipleOwners).toString(),
          ccorp: Boolean(taxEntityType === C_CORP_TAX_ENTITY).toString(),
          sharedAccounts: Boolean(
            !accountsSeparate && !willingAccountsSeparate
          ).toString(),
          scorpEntireYear: Boolean(scorpEntireYear).toString(),
          otherSelection: Boolean(otherDisq).toString(),
        })
      } else if (
        over10Employees ||
        numberOfEmployees === NumberOfEmployees.tenOrMore
      ) {
        navigate(SIGNUP_PATHS.waitlist)
      } else if (navToConsult) {
        navigate(SIGNUP_PATHS.consult)
      } else {
        const taxEntityValue =
          taxEntityType === 'other'
            ? TAX_ENTITY_TYPES.schedule_c
            : taxEntityType
        const res = await updateFinancialProfile(financialProfile?.id, {
          accountsSeparate,
          willingAccountsSeparate,
          businessEntity,
          taxEntityType: taxEntityValue,
          fileInternationalTaxes,
          approximatePracticeStartDate,
          practiceType: enableStarterPlans
            ? numberOfEmployees === NumberOfEmployees.justMe
              ? PracticeType.solo
              : PracticeType.group
            : undefined,
          practiceSize: enableStarterPlans ? numberOfEmployees : undefined,
        })(dispatch)

        if (res) {
          if (shouldEnableAutoReconCUBK2) {
            await generateBookkeepingReport()(dispatch)
          }
          navigate(SIGNUP_PATHS.practiceJourney)
        }
      }
    },
  })

  const { values, submitForm, isSubmitting, isValid } = formik

  return (
    <Container style={{ paddingTop: 64 }}>
      <FormikProvider value={formik}>
        <Grid stackable>
          <span ref={scrollRef} />
          <SignupHeader currentStep={1} />
          <GridRowColumn>
            {freeTrialEnabled && <FreeTrialSignupBanner />}
          </GridRowColumn>

          <GridRowColumn>
            <Text as="h1">Tell us a little bit more about your practice</Text>
          </GridRowColumn>
          <GridRowColumn short>
            <Text>
              To serve you accurately, we need a clear picture of your practice
              — please answer each question carefully
            </Text>
          </GridRowColumn>

          <Grid.Row>
            <Grid.Column width={8}>
              <Grid>
                <GridRowColumn>
                  <FormikLabelError
                    name={getFieldName<typeof values>('businessEntity')}
                    label="How is your practice set up?"
                    schema={makeReqStringSchema({})}
                    required
                  />
                  <FormikRadioButton
                    name={getFieldName<typeof values>('businessEntity')}
                    value="sprop"
                    label="Sole Proprietor"
                    style={{ marginTop: 8 }}
                  />
                  <br />
                  <FormikRadioButton
                    name={getFieldName<typeof values>('businessEntity')}
                    value="llc_single"
                    label="LLC"
                    style={{ marginTop: 8 }}
                  />
                  <br />
                  <FormikRadioButton
                    name={getFieldName<typeof values>('businessEntity')}
                    value="pllc_single"
                    label="PLLC"
                    style={{ marginTop: 8 }}
                  />
                  <br />
                  <FormikRadioButton
                    name={getFieldName<typeof values>('businessEntity')}
                    value="c_corp"
                    label={
                      <>
                        C Corporation (or Professional Corporation)
                        <Popup
                          content={
                            <>
                              <Text as="h3">C Corporation</Text>
                              <br />
                              <Text>
                                Business Entities that are considered C
                                Corporations typically include:
                              </Text>
                              <ul>
                                <li>
                                  <Text>Professional Corporation</Text>
                                </li>
                                <li>
                                  <Text>General Stock Corporation</Text>
                                </li>
                                <li>
                                  <Text>Domestic Corporation</Text>
                                </li>
                              </ul>
                              <Text>
                                Note: Depending on the state where your entity
                                is formed, we may not be able to support General
                                or Domestic Corporations. We can work with you
                                to determine if you qualify.
                              </Text>
                            </>
                          }
                        />
                      </>
                    }
                    style={{ marginTop: 8 }}
                  />
                  <br />
                  <FormikRadioButton
                    name={getFieldName<typeof values>('businessEntity')}
                    value="other"
                    label="Other"
                    style={{ marginTop: 8 }}
                  />
                </GridRowColumn>
                <GridRowColumn>
                  <FormikLabelError
                    name={getFieldName<typeof values>('taxEntityType')}
                    schema={makeReqStringSchema({})}
                    afterLabel={
                      <Popup
                        content={
                          <>
                            <Text as="h3">Tax entities</Text>
                            <br />
                            <Text>
                              <b>Sole Proprietor</b> - You personally own and
                              operate your business and your taxes are reported
                              as personal income
                            </Text>
                            <Text>
                              <b>S Corporation</b> - Your business’s corporate
                              income, losses, deductions, and credit are passed
                              through shareholders for federal tax purposes
                            </Text>
                            <Text>
                              <b>C Corporation</b> - Your business’s owners, or
                              shareholders, are taxed separately from the
                              entity.
                            </Text>
                            <Divider />
                            <Text>
                              If none of the above applies or if you aren’t
                              sure, select <b>Other</b>.
                            </Text>
                          </>
                        }
                      />
                    }
                    label="Your practice is taxed as a..."
                    required
                  />
                  <FormikRadioButton
                    name={getFieldName<typeof values>('taxEntityType')}
                    value="form_1040"
                    label="Sole Proprietor"
                    style={{ marginTop: 8 }}
                  />
                  <br />
                  <FormikRadioButton
                    name={getFieldName<typeof values>('taxEntityType')}
                    value="form_1120_s"
                    label="S Corporation"
                    style={{ marginTop: 8 }}
                  />
                  <br />
                  <FormikRadioButton
                    name={getFieldName<typeof values>('taxEntityType')}
                    value="form_1120"
                    label="C Corporation"
                    style={{ marginTop: 8 }}
                  />
                  <br />
                  <FormikRadioButton
                    name={getFieldName<typeof values>('taxEntityType')}
                    label="Other"
                    style={{ marginTop: 8 }}
                    value="other"
                  />
                </GridRowColumn>
              </Grid>
            </Grid.Column>
            <Grid.Column width={8}>
              <Image
                src="https://heard-images.s3.amazonaws.com/assets/couch.svg"
                style={{
                  height: 486,
                  width: 486,
                  // This image has a lot of extra padding - this removes that
                  marginTop: -93,
                  marginBottom: -75,
                }}
              />
            </Grid.Column>
          </Grid.Row>

          <br />
          {values.businessEntity === 'c_corp' &&
            values.taxEntityType === 'form_1120_s' && (
              <QuestionRow
                name={getFieldName<typeof values>('scorpEntireYear')}
                label={`Has your practice been taxed as an S Corporation for the entirety of ${DateTime.now().year}?`}
                required
                popupContent={
                  <>
                    <Text as="h3">Becoming an S Corporation</Text>
                    <br />
                    <Text>
                      Was your business an S Corporation from January 1,{' '}
                      {DateTime.now().year}, through December 31,{' '}
                      {DateTime.now().year}? If you elected S Corporation status
                      partway through the year, did you receive approval for
                      full-year S Corporation status?
                    </Text>
                  </>
                }
              />
            )}

          <GridRowColumn>
            <div className="monthPicker">
              <FormikDateInput
                label="When did you start your practice?"
                name={getFieldName<typeof values>(
                  'approximatePracticeStartDate'
                )}
                placeholder="Select month"
                dateFormat={DATE_FORMATS.YEAR_MONTH}
                showMonthYearPicker
                maxDate={new Date()}
                schema={makeReqStringSchema({})}
                required
              />
            </div>
          </GridRowColumn>
          {enableStarterPlans && (
            <GridRowColumn>
              <FormikLabelError
                name={getFieldName<typeof values>('numberOfEmployees')}
                label="How many employees are in your practice?"
                schema={makeReqStringSchema({ field: 'number of employees' })}
                required
              />
              <FormikRadioButton
                name={getFieldName<typeof values>('numberOfEmployees')}
                value={NumberOfEmployees.justMe}
                label="Just me"
                style={{ marginTop: 8 }}
              />
              <br />
              <FormikRadioButton
                name={getFieldName<typeof values>('numberOfEmployees')}
                value={NumberOfEmployees.twoToTen}
                label="I have between 1-9 employees and contractors."
                style={{ marginTop: 8 }}
              />
              <br />
              <FormikRadioButton
                name={getFieldName<typeof values>('numberOfEmployees')}
                value={NumberOfEmployees.tenOrMore}
                label="I have 10 or more employees and contractors."
                style={{ marginTop: 8 }}
              />
            </GridRowColumn>
          )}
          <>
            <GridRowColumn>
              <Text as="h1">Do any of these situations apply to you?</Text>
            </GridRowColumn>
            <GridRowColumn>
              <FormikLabelError
                name={getFieldName<typeof values>('accountsSeparate')}
                schema={makeReqBoolSchema()}
                label="How do you pay for business expenses?"
                required
                afterLabel={
                  <Popup
                    content={
                      <>
                        <Text as="h3">Commingled banking activity</Text>
                        <br />
                        <Text>
                          If you have personal and business transactions in the
                          same bank account, select <b>Personal bank account</b>
                          .
                        </Text>
                      </>
                    }
                  />
                }
              />
            </GridRowColumn>
            <Grid.Row className="short">
              <Grid.Column computer={6} tablet={6} mobile={8}>
                <FormikRadioToggleButton
                  name={getFieldName<typeof values>('accountsSeparate')}
                  value
                  fullWidth
                >
                  Business bank account
                </FormikRadioToggleButton>
              </Grid.Column>
              <Grid.Column computer={6} tablet={6} mobile={8}>
                <FormikRadioToggleButton
                  name={getFieldName<typeof values>('accountsSeparate')}
                  value={false}
                  fullWidth
                >
                  Personal bank account
                </FormikRadioToggleButton>
              </Grid.Column>
            </Grid.Row>
          </>
          {values.accountsSeparate === false && (
            <QuestionRow
              name={getFieldName<typeof values>('willingAccountsSeparate')}
              schema={yup
                .boolean()
                .nullable()
                .when('accountsSeparate', ([accountsSeparate], schema) =>
                  accountsSeparate === false
                    ? yup.boolean().nullable().required()
                    : schema
                )}
              label="Are you willing to separate your personal and business banking activity?"
              popupContent={
                <>
                  <Text as="h3">
                    Separating your personal and business banking activity
                  </Text>
                  <br />
                  <Text>
                    Paying for business and personal expenses from the same
                    account puts your practice at risk.
                  </Text>
                  <br />
                  <Text>
                    We&apos;ll guide you through separating your transactions
                    after you’ve signed up.
                  </Text>
                </>
              }
            />
          )}
          <QuestionRow
            name={getFieldName<typeof values>('multipleOwners')}
            schema={makeReqBoolSchema()}
            label="Does your practice have multiple owners (other than a current spouse)?"
            popupContent={
              <>
                <Text as="h3">Multiple owners</Text>
                <br />
                <Text>
                  Select <b>yes</b> if you share ownership of your practice with
                  anyone else, including your spouse.
                </Text>
              </>
            }
          />
          {!enableStarterPlans && (
            <QuestionRow
              name={getFieldName<typeof values>('over10Employees')}
              schema={makeReqBoolSchema()}
              label="Does your practice have more than 10 employees?"
              popupContent={
                <>
                  <Text as="h3">Number of employees</Text>
                  <br />
                  <Text>
                    If you have more than 10 full-time employees, select{' '}
                    <b>Yes</b>.
                  </Text>
                </>
              }
            />
          )}
          <QuestionRow
            name={getFieldName<typeof values>('fileInternationalTaxes')}
            schema={makeReqBoolSchema()}
            label="Does your practice need to pay international taxes?"
            popupContent={
              <>
                <Text as="h3">International taxes</Text>
                <br />
                <Text>
                  If you see international clients and your practice pays taxes
                  to countries other than the United States (including U.S.
                  territories) select <b>Yes</b>.
                </Text>
              </>
            }
          />
          <Grid.Row />

          <Divider />
          <Grid.Row />
          <GridRowColumn spacer={14} width={2}>
            <Button
              fullWidth
              onClick={submitForm}
              disabled={!isValid || isSubmitting}
              id="btn-signup-about-practice"
              loading={isSubmitting}
            >
              Continue
            </Button>
          </GridRowColumn>
          <Grid.Row />
        </Grid>
      </FormikProvider>
    </Container>
  )
}

export default AboutYourPractice
